import React, { FC, useEffect, useRef, useState } from 'react'
import { useStore } from 'store'
import cn from 'classnames'
import { toast } from 'App'
import { CheckBox } from 'containers/Notifications/components/NotificationsTable/CheckBox'
import { ConfirmModal } from 'components/ConfirmModal'
import ButtonIcon from 'components/ButtonIcon'

import { checkBadWords } from 'utils'
import { name } from 'utils/regExp'

import { ReactComponent as ArrowDown } from 'sources/images/arrowDown.svg'
import { ReactComponent as Close } from 'sources/images/close.svg'
import { ReactComponent as SearchIcon } from 'sources/images/search.svg'

import styles from './styles.module.scss'
import { observer } from 'mobx-react'

interface IProps {
  isOpenSelect: boolean
  onClickSelect: () => void
  options: IOption[]
  setOptions: React.Dispatch<React.SetStateAction<IOption[]>>
  selectedOptions: IOption[]
  setSelectedOptions: React.Dispatch<React.SetStateAction<IOption[]>>
  multipleSelect?: boolean
  selectTitle: string
  addButton?: boolean
  searchBlock?: boolean
  // maxHeightCategoriesBlock: number
}

export interface IOption {
  Id: string,
  Name: string
  IsDefault: boolean
}

const optionAll = { Id: 'All', Name: 'All', IsDefault: true }

export const OwnSelect: FC<IProps> = observer((
  {
    isOpenSelect,
    onClickSelect,
    options,
    setOptions,
    selectedOptions,
    setSelectedOptions,
    multipleSelect,
    selectTitle,
    addButton,
    searchBlock
    // maxHeightCategoriesBlock,
  }) => {
  const { videoChat, loading, mediaQuery } = useStore()
  const [searchValue, setSearchValue] = useState('')

  const [filteredOptions, setFilteredOptions] = useState<IOption[]>([])

  const [isOpenNewOptionModal, setIsOpenNewOptionModal] = useState(false)
  const [newOption, setNewCategory] = useState('')

  const categoryContainerRef = useRef<HTMLDivElement | null>(null)


  useEffect(() => {
    handlerFilterOptions('', options)
  }, [options])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (categoryContainerRef.current && !categoryContainerRef.current.contains(e.target as Node)) {
        onClickSelect()
      }
    }

    categoryContainerRef.current && isOpenSelect && document.addEventListener('mousedown', handleClick)

    return () => {
      categoryContainerRef.current && document.removeEventListener('mousedown', handleClick)
    }

  }, [categoryContainerRef.current, isOpenSelect])

  const sortCategories = (data: IOption[]) => {

    const getSortCategories = (data: IOption[]) => {
      return data.sort((a, b) => {
        return (a.Name || 'Ω').localeCompare((b.Name || 'Ω'), undefined, {
          sensitivity: 'accent',
          ignorePunctuation: true
        })
      })
    }

    const defaultCategories = getSortCategories(data.filter(item => item.IsDefault))
    const customCategories = getSortCategories(data.filter(item => !item.IsDefault))

    return [...defaultCategories, ...customCategories]
  }

  const handlerFilterOptions = (searchValue: string, options: IOption[]) => {
    if (searchValue) {
      const filtered = options.filter((option) => {
        return option.Name.toLowerCase().includes(searchValue.toLowerCase())
      })
      setFilteredOptions(sortCategories(filtered))
    } else {
      if (options.length === 0) {
        setFilteredOptions([])
      } else {
        setFilteredOptions(sortCategories(options))
      }
    }
  }

  const onChangeSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value
    handlerFilterOptions(searchValue, options)
    setSearchValue(searchValue)
  }

  const clearSearchValue = () => {
    handlerFilterOptions('', options)
    setSearchValue('')
  }

  const onClickCheckBox = (key?: number | string) => {
    if (typeof key === 'string') {
      if (multipleSelect) {
        setSelectedOptions(prev => {
          const filtered = prev.filter(value => value.Name !== key)
          return filtered.length === prev.length ? prev.concat(options.filter(item => item.Name === key)) : filtered
        })
      } else {
        setSelectedOptions(filteredOptions.filter(value => value.Name === key))
      }
    }
  }

  const onChangeNewOptionInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewCategory(e.target.value)
  }

  const addNewOption = async () => {
    if (newOption.length < 2) {
      return toast({
        type: 'warning',
        message: 'Category should be longer than 2 symbols'
      })
    }
    if (newOption.length > 40) {
      return toast({
        type: 'warning',
        message: 'Category cannot be longer than 40 symbols'
      })
    }

    if (checkBadWords(newOption)) {
      return toast({
        type: 'warning',
        message: 'You are not allowed to use prohibited words'
      })
    }

    if ((/\s/.test(newOption as string))) {
      return toast({
        type: 'warning',
        message: 'You are not allowed to use empty space'
      })
    }

    if (!Boolean(name(newOption))) {
      return toast({
        type: 'warning',
        message: 'You are not allowed to use emojis, numbers, and smiles'
      })
    }

    try {
      loading.setLoading(true)
      const category = await videoChat.addCategory(newOption)
      const newCategories = [...options, category]
      setOptions(newCategories)
      handlerFilterOptions(searchValue, newCategories)
      setIsOpenNewOptionModal(false)
      setNewCategory('')
      loading.setLoading(false)
      toast({
        type: 'success',
        message: 'Category successfully added'
      })
    } catch (e: any) {
      loading.setLoading(false)
      console.log('e', e)
      toast({
        type: 'error',
        message: e.message
      })
    }
  }

  const clearInputField = () => {
    setNewCategory('')
  }

  const onClickAddCategoryButton = () => {
    setIsOpenNewOptionModal(prev => !prev)
  }

  // Change classes name
  return (
    <div
      // style={{ maxHeight: maxHeightCategoriesBlock > 0 ? maxHeightCategoriesBlock + 58 : 50 }}
      ref={categoryContainerRef}
      className={cn({
        [styles.categoryContainer]: true
      })}
    >

      <div className={styles.categoriesSelect} onClick={onClickSelect}>
        <div className={styles.categorySelected}>
          {selectedOptions.length
            ? selectedOptions.map(item => item.Name).join(', ')
            : selectTitle}
        </div>
        <ArrowDown className={cn({
          [styles.selectArrow]: true,
          [styles.selectArrowUp]: isOpenSelect
        })}/>
      </div>

      <div
        // style={{ maxHeight: maxHeightCategoriesBlock }}
        className={cn({
          [styles.categoriesBlock]: true,
          [styles.categoriesBlockOpen]: isOpenSelect
        })}>

        {searchBlock
          ? <div className={cn({
            [styles.searchBlock]: true
          })}>
            <input value={searchValue}
                   autoComplete="off"
                   onChange={onChangeSearchValue}
                   className={cn({
                     [styles.input]: true
                   })}
                   type="text"
                   name="search"
                   placeholder="Search"
            />
            <Close
              style={{ display: searchValue.length ? 'block' : 'none' }}
              className={cn({
                [styles.closeIcon]: true
              })}
              onClick={clearSearchValue}
            />
            <SearchIcon style={{ display: searchValue.length ? 'none' : 'block' }}
                        className={cn({
                          [styles.searchIcon]: true
                        })}/>
          </div>
          : null}

        {addButton ? <ButtonIcon
          onClick={onClickAddCategoryButton}
          style={{
            position: 'absolute',
            minWidth: mediaQuery.minWidth_2200 ? '28px' : '24px',
            width: mediaQuery.minWidth_2200 ? '28px' : '24px',
            height: mediaQuery.minWidth_2200 ? '28px' : '24px',
            bottom: '9px',
            right: '15px'
          }}
          styleIcon={{
            width: mediaQuery.minWidth_2200 ? '18px' : '16px',
            height: mediaQuery.minWidth_2200 ? '18px' : '16px'
          }}
          icon={'plus'}
          tooltip="Click to add category"
          tooltipPosition="left"
          // tooltipDisablePortal={false}
          bordered
        /> : null}

        <div
          // style={{ maxHeight: maxHeightCategoriesBlock - 41 }}
          className={cn({
            [styles.categoriesList]: true,
            [styles.categoriesListEmpty]: filteredOptions.length === 0
          })}>
          <div className={styles.categories}>
            {filteredOptions.map(option => (
              <div className={styles.categoryItem} key={option.Id}>
                <CheckBox active={selectedOptions.some(selectedOption => selectedOption.Name === option.Name)}
                          id={option.Name}
                          onClick={onClickCheckBox}/>
                <div onClick={() => onClickCheckBox(option.Name)} className={styles.categoryTitle}>{option.Name}</div>
              </div>
            ))}
            {filteredOptions.length === 0 ? <div className={styles.noCategories}>No items found</div> : null}
          </div>
        </div>
      </div>

      {/*Need make not only for category */}
      {/*Add new category modal*/}
      <ConfirmModal isOpen={isOpenNewOptionModal}
                    blockOutsideClose
                    setIsOpen={setIsOpenNewOptionModal}
                    onClickYes={addNewOption}
                    onClickNo={clearInputField}
                    closeOnYes={false}
                    textButton1="Add"
                    textButton2="Cancel"
                    style={{ maxWidth: 'calc(min(410px, 95vw))' }}
                    text={(
                      <div className={styles.newOptionModalBody}>
                        <div className={styles.title}>
                          Enter a category name
                        </div>
                        <div className={styles.categoryInputBox}>
                          <input
                            onChange={onChangeNewOptionInput}
                            value={newOption}
                            maxLength={40}
                            className={cn({ [styles.optionInput]: true })}
                            type="text"
                            placeholder="Category"
                            name="category"
                          />
                        </div>
                      </div>
                    )}/>
    </div>
  )
})
